(() => {
  const params = new URLSearchParams(window.location.search);
  if (window.location.pathname !== '/collections/all' || params.get('ss_finder') !== '1') return;

  const ANSWER_LABELS = {
    side: { light: 'Light side', dark: 'Dark side', either: 'Either side' },
    use: { dueling: 'Dueling', display: 'Display / replica', both: 'Dueling + display' },
    budget: { '200': 'Under $200', '300': 'Under $300', '500': 'Under $500', flexible: 'Flexible budget' },
    color: { blue: 'Blue blade', green: 'Green blade', red: 'Red blade', purple: 'Purple blade', yellow: 'Yellow blade', white: 'White blade', any: 'Any color' }
  };

  const CHARACTER_LABELS = {
    any: 'Any character', 'ahsoka-tano': 'Ahsoka Tano', 'anakin-skywalker': 'Anakin Skywalker',
    'asajj-ventress': 'Asajj Ventress', 'baylan-skoll': 'Baylan Skoll', 'cal-kestis': 'Cal Kestis',
    'count-dooku': 'Count Dooku', 'darth-maul': 'Darth Maul', 'darth-vader': 'Darth Vader',
    'ezra-bridger': 'Ezra Bridger', 'kanan-jarrus': 'Kanan Jarrus', 'kylo-ren': 'Kylo Ren',
    'luke-skywalker': 'Luke Skywalker', 'mace-windu': 'Mace Windu', 'mara-jade': 'Mara Jade',
    'obi-wan-kenobi': 'Obi-Wan Kenobi', 'princess-leia': 'Princess Leia', 'qui-gon-jinn': 'Qui-Gon Jinn',
    revan: 'Revan', ronin: 'Ronin', 'sabine-wren': 'Sabine Wren', 'satele-shan': 'Satele Shan',
    'shin-hati': 'Shin Hati', starkiller: 'Starkiller', 'taron-malicos': 'Taron Malicos',
    'temple-guard': 'Temple Guard', yoda: 'Yoda'
  };

  const answers = {
    side: params.get('side') || 'either',
    use: params.get('use') || 'both',
    budget: params.get('budget') || 'flexible',
    characters: (params.get('characters') || 'any').split(',').filter(Boolean),
    color: params.get('color') || 'any'
  };

  const hasTerm = (text, terms) => terms.some((term) => text.includes(term));

  const scoreCard = (item) => {
    const card = item.querySelector('product-card');
    if (!card) return { score: -999, price: Number.MAX_SAFE_INTEGER };

    const collections = (card.dataset.productCollections || '').toLowerCase().split('|');
    const tags = (card.dataset.productTags || '').toLowerCase();
    const title = (card.dataset.productTitle || '').toLowerCase();
    const type = (card.dataset.productType || '').toLowerCase();
    const text = `${collections.join('|')}|${tags}|${title}|${type}`;
    const price = Number(card.dataset.productPrice || Number.MAX_SAFE_INTEGER);
    let score = 0;

    if (answers.side === 'light') {
      if (hasTerm(text, ['jedi', 'light-side', 'rebel-alliance'])) score += 32;
      if (hasTerm(text, ['sith', 'dark-side'])) score -= 10;
    } else if (answers.side === 'dark') {
      if (hasTerm(text, ['sith', 'dark-side'])) score += 32;
      if (hasTerm(text, ['jedi', 'light-side'])) score -= 10;
    }

    if (answers.use === 'dueling') {
      if (hasTerm(text, ['dueling', 'duel-ready', 'rgb-baselit', 'baselit'])) score += 26;
      if (hasTerm(text, ['replica'])) score -= 4;
    } else if (answers.use === 'display') {
      if (hasTerm(text, ['replica', 'character', 'collector', 'screen'])) score += 26;
      if (hasTerm(text, ['dueling'])) score -= 3;
    } else {
      if (hasTerm(text, ['dueling', 'replica'])) score += 9;
    }

    const chosenCharacters = answers.characters.filter((character) => character !== 'any');
    chosenCharacters.forEach((character) => {
      if (collections.includes(character)) score += 65;
      else if (text.includes(character)) score += 42;
      else if (title.includes(character.replaceAll('-', ' '))) score += 42;
    });

    if (answers.color !== 'any') {
      const colorTerms = [answers.color, `${answers.color}-lightsabers`, `${answers.color}-saber`];
      if (hasTerm(text, colorTerms)) score += 27;
    }

    if (answers.budget !== 'flexible') {
      const ceiling = Number(answers.budget) * 100;
      if (price <= ceiling) {
        score += 30;
        score += Math.max(0, Math.round((ceiling - price) / 2500));
      } else {
        score -= Math.min(45, Math.ceil((price - ceiling) / 2000) * 5);
      }
    } else if (Number.isFinite(price)) {
      score += Math.max(0, 8 - Math.floor(price / 10000));
    }

    item.dataset.ssFinderScore = String(score);
    return { score, price };
  };

  const buildBanner = (total) => {
    const banner = document.createElement('section');
    banner.className = 'ss-finder-results';
    banner.setAttribute('aria-labelledby', 'SSFinderResultsTitle');

    const chips = [
      ANSWER_LABELS.side[answers.side],
      ANSWER_LABELS.use[answers.use],
      ANSWER_LABELS.budget[answers.budget],
      ...answers.characters.map((character) => CHARACTER_LABELS[character] || character),
      ANSWER_LABELS.color[answers.color]
    ].filter(Boolean);

    banner.innerHTML = `
      <div class="ss-finder-results__stripe" aria-hidden="true"></div>
      <div class="ss-finder-results__body">
        <div>
          <span class="ss-finder-results__eyebrow">Personalized first-saber finder</span>
          <h2 id="SSFinderResultsTitle">Your best saber matches</h2>
          <p>We ranked all ${total} sabers using your character, use, budget, side, and blade-color preferences. Strongest overall matches appear first.</p>
          <div class="ss-finder-results__chips">${chips.map((chip) => `<span class="ss-finder-results__chip">${chip}</span>`).join('')}</div>
        </div>
        <a class="ss-finder-results__retake" href="/pages/first-saber-finder">Retake finder <span aria-hidden="true">→</span></a>
      </div>`;
    return banner;
  };

  const init = async () => {
    const results = document.querySelector('results-list');
    const meta = document.querySelector('[data-ss-finder-results-meta]');
    const grid = document.querySelector('.product-grid') || document.querySelector('#ResultsList');
    if (!results || !meta || !grid) return;

    results.setAttribute('infinite-scroll', 'false');
    results.classList.add('ss-finder-is-ranking');

    const wrapper = grid.closest('.collection-wrapper');
    const total = Number(meta.dataset.products || grid.children.length);
    const status = document.createElement('p');
    status.className = 'ss-finder-results-status';
    status.setAttribute('aria-live', 'polite');
    status.textContent = `Ranking ${total} sabers from your answers…`;
    grid.parentNode.insertBefore(status, grid);

    const pageCount = Number(meta.dataset.pages || 1);
    const sectionId = results.getAttribute('section-id');
    const knownIds = new Set([...grid.querySelectorAll('[data-product-id]')].map((item) => item.dataset.productId));

    if (pageCount > 1 && sectionId) {
      const requests = [];
      for (let page = 2; page <= pageCount; page += 1) {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', sectionId);
        requests.push(fetch(url, { credentials: 'same-origin' }).then((response) => response.ok ? response.text() : ''));
      }

      const pages = await Promise.all(requests);
      pages.forEach((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingGrid = doc.querySelector('.product-grid') || doc.querySelector('#ResultsList');
        incomingGrid?.querySelectorAll('[data-product-id]').forEach((item) => {
          if (knownIds.has(item.dataset.productId)) return;
          knownIds.add(item.dataset.productId);
          grid.appendChild(item);
        });
      });
    }

    const ranked = [...grid.querySelectorAll(':scope > [data-product-id]')]
      .map((item, originalIndex) => ({ item, originalIndex, ...scoreCard(item) }))
      .sort((a, b) => b.score - a.score || a.price - b.price || a.originalIndex - b.originalIndex);

    ranked.forEach(({ item }, index) => {
      item.style.order = String(index);
      grid.appendChild(item);
    });

    results.querySelectorAll('pagination-component, .pagination, [data-pagination]').forEach((node) => node.remove());
    if (wrapper) wrapper.insertBefore(buildBanner(ranked.length), wrapper.firstChild);
    status.textContent = `${ranked.length} sabers ranked from your answers.`;
    results.classList.remove('ss-finder-is-ranking');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
